import React, { useCallback, useEffect, useState } from "react";
import {
  Calculator, Buildings, Plus, Trash, X, CircleNotch, Books, Stack, Receipt,
  Package, ChartBar, Bank, Coins, Target, ListChecks, FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Ledgers from "./accounting/Ledgers";
import Vouchers from "./accounting/Vouchers";
import Invoicing from "./accounting/Invoicing";
import Inventory from "./accounting/Inventory";
import Reports from "./accounting/Reports";
import Banking from "./accounting/Banking";
import CostBudget from "./accounting/CostBudget";
import GstReturns from "./accounting/GstReturns";
import Dashboard from "./accounting/Dashboard";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: ChartBar },
  { id: "ledgers", label: "Chart of Accounts", icon: Books },
  { id: "vouchers", label: "Vouchers & Day Book", icon: Stack },
  { id: "invoicing", label: "GST Invoicing", icon: Receipt },
  { id: "inventory", label: "Inventory", icon: Package },
  { id: "reports", label: "Reports", icon: ListChecks },
  { id: "gstreturns", label: "GST Returns", icon: Receipt },
  { id: "banking", label: "Bank Recon", icon: Bank },
  { id: "costbudget", label: "Cost & Budget", icon: Target },
];

const STATES = ["Andhra Pradesh", "Assam", "Bihar", "Delhi", "Gujarat", "Haryana", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab", "Rajasthan", "Tamil Nadu",
  "Telangana", "Uttar Pradesh", "West Bengal"];

export default function Accounting() {
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState("");
  const [tab, setTab] = useState("dashboard");
  const [showCo, setShowCo] = useState(false);
  const [form, setForm] = useState({ name: "", state: "West Bengal", gstin: "", fy_start: "2025-04-01", address: "" });
  const [saving, setSaving] = useState(false);

  const loadCompanies = useCallback(async () => {
    try {
      const r = await api.get("/acc/companies");
      setCompanies(r.data);
      if (r.data.length) setCid((c) => c || r.data[0].company_id);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const saveCompany = async () => {
    if (!form.name.trim()) { toast.error("Enter a company name"); return; }
    setSaving(true);
    try {
      const r = await api.post("/acc/companies", form);
      toast.success("Company created — books seeded");
      setShowCo(false); setForm({ name: "", state: "West Bengal", gstin: "", fy_start: "2025-04-01", address: "" });
      await loadCompanies();
      setCid(r.data.company_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const delCompany = async (id) => {
    if (!window.confirm("Delete this company and all its books?")) return;
    try { await api.delete(`/acc/companies/${id}`); toast.success("Deleted"); if (cid === id) setCid(""); await loadCompanies(); }
    catch { toast.error("Failed"); }
  };

  const company = companies.find((c) => c.company_id === cid);

  return (
    <div data-testid="accounting-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#2A2625] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Calculator size={26} weight="duotone" className="text-[#F29A86]" /> Zeyrodha Accounting
          </h1>
          <p className="text-sm text-[#8C8381] mt-1">Double-entry books, GST invoicing, inventory, cost centres, budgets & ratios — zero-error deterministic engine.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="company-select" value={cid} onChange={(e) => setCid(e.target.value)}
            className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5] min-w-[180px]">
            <option value="">Select company…</option>
            {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
          <button data-testid="add-company-btn" onClick={() => setShowCo(true)}
            className="flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-4 py-2.5 transition-colors">
            <Plus size={16} weight="bold" /> New company
          </button>
        </div>
      </div>

      {!cid ? (
        <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-10 text-center" data-testid="no-company">
          <Buildings size={40} weight="duotone" className="text-[#F29A86] mx-auto mb-3" />
          <p className="text-[#2A2625] font-medium">No company selected</p>
          <p className="text-sm text-[#8C8381] mt-1">Create a company to start keeping books. Standard ledgers (Cash, Sales, Purchase, GST) are auto-created.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {company && (
              <span className="text-xs text-[#8C8381]">
                {company.state} · {company.gstin || "No GSTIN"} · FY from {company.fy_start}
                <button data-testid="del-company-btn" onClick={() => delCompany(cid)} className="ml-2 text-[#8C8381] hover:text-[#E09494] align-middle"><Trash size={14} /></button>
              </span>
            )}
          </div>
          <div className="flex gap-1 overflow-x-auto border-b border-[#2A2625]/10 mb-5" data-testid="acc-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} data-testid={`acc-tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    tab === t.id ? "border-[#F29A86] text-[#2A2625]" : "border-transparent text-[#8C8381] hover:text-[#2A2625]"}`}>
                  <Icon size={16} weight={tab === t.id ? "duotone" : "regular"} /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === "dashboard" && <Dashboard cid={cid} />}
          {tab === "ledgers" && <Ledgers cid={cid} />}
          {tab === "vouchers" && <Vouchers cid={cid} />}
          {tab === "invoicing" && <Invoicing cid={cid} company={company} />}
          {tab === "inventory" && <Inventory cid={cid} />}
          {tab === "reports" && <Reports cid={cid} />}
          {tab === "gstreturns" && <GstReturns cid={cid} />}
          {tab === "banking" && <Banking cid={cid} />}
          {tab === "costbudget" && <CostBudget cid={cid} />}
        </>
      )}

      {showCo && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShowCo(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="company-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2A2625]">New company</h3>
              <button onClick={() => setShowCo(false)}><X size={18} className="text-[#8C8381]" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="co-name" placeholder="Company / firm name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="co-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
                  {STATES.map((s) => <option key={s}>{s}</option>)}
                </select>
                <input data-testid="co-fy" type="date" value={form.fy_start} onChange={(e) => setForm({ ...form, fy_start: e.target.value })}
                  className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
              </div>
              <input data-testid="co-gstin" placeholder="GSTIN (optional)" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                className="w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
              <input data-testid="co-address" placeholder="Address (optional)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
            </div>
            <button data-testid="save-company-btn" onClick={saveCompany} disabled={saving}
              className="mt-5 w-full flex items-center justify-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-5 py-2.5 transition-colors disabled:opacity-60">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />} Create company
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
